/* 🚀 BuildStatusBanner — owner-only "is production stale?" indicator
 * -------------------------------------------------------------------
 * Fetches the current backend git HEAD from BOTH:
 *   • the backend we're currently talking to (production if on prod domain)
 *   • the preview backend URL (hardcoded — this is where the agent works)
 * If the two commits differ, we show a friendly nudge to redeploy.
 *
 * Non-owners never see this. In preview, we suppress it (no reason to nag
 * yourself). Only appears on the production domain for the owner role.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const OWN_API     = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Where the agent's live preview lives. Cross-origin fetch is fine because
// the backend CORS is `*` and this endpoint is public.
const PREVIEW_API = "https://omni-brand-1.preview.emergentagent.com/api";

// Detect whether we're currently on the production domain.
const isProductionDomain = () => {
  try {
    return /omnimuscleholistichealing\.com$/i.test(window.location.hostname);
  } catch { return false; }
};

async function fetchBuild(base) {
  try {
    // withCredentials:false — this is a cross-origin public GET, no cookie.
    const r = await axios.get(`${base}/build/info`, {
      withCredentials: false, timeout: 4000,
    });
    return r.data || null;
  } catch { return null; }
}

function commitsAgo(prodIso, previewIso) {
  if (!prodIso || !previewIso) return null;
  const ms = new Date(previewIso).getTime() - new Date(prodIso).getTime();
  if (isNaN(ms) || ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60)  return `${mins}m`;
  const hrs  = Math.round(mins / 60);
  if (hrs  < 48) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  return `${days}d`;
}

export default function BuildStatusBanner() {
  const { user } = useAuth();
  const [own,     setOwn]     = useState(null);
  const [preview, setPreview] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "owner") return;
    // Fire both requests in parallel
    fetchBuild(OWN_API).then(setOwn);
    // Skip preview call if we ARE preview (avoids duplicate + noise)
    if (isProductionDomain()) fetchBuild(PREVIEW_API).then(setPreview);
  }, [user]);

  if (!user || user.role !== "owner") return null;
  if (dismissed) return null;
  if (!own) return null;

  const onProd = isProductionDomain();
  const stale  = onProd && preview && own.commit && preview.commit && own.commit !== preview.commit;
  const gap    = stale ? commitsAgo(own.commit_date, preview.commit_date) : null;

  // On preview (or when in-sync): show a small info chip so owner can eyeball
  // the current build hash while comparing environments side-by-side.
  if (!stale) {
    return (
      <div
        data-testid="build-status-chip"
        className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3 inline-flex items-center gap-2 rounded-full border border-neutral-800 bg-black/60 px-3 py-1"
        title={`${own.commit_short} · ${own.message || ""}`}
      >
        🚀 Build <span className="text-amber-300 font-bold">{own.commit_short}</span>
        {onProd ? " (production · in sync)" : " (preview)"}
      </div>
    );
  }

  return (
    <div
      data-testid="build-status-stale-banner"
      className="mb-4 rounded-xl border-2 border-amber-500/60 bg-gradient-to-r from-amber-950/60 via-amber-900/40 to-amber-950/60 p-4 shadow-[0_0_20px_rgba(245,158,11,0.25)]"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-amber-200 font-black text-sm uppercase tracking-wider inline-flex items-center gap-2">
            ⚠️ Production is behind preview
          </div>
          <div className="text-[11px] text-amber-100/85 mt-1.5 leading-relaxed">
            Preview build <span className="font-bold text-amber-200">{preview.commit_short}</span>
            {" "}has newer code than production
            {" "}<span className="font-bold text-amber-200">{own.commit_short}</span>
            {gap ? <> · <span className="font-bold">{gap}</span> ahead</> : null}.
          </div>
          {preview.message && (
            <div className="text-[10px] text-amber-100/60 mt-1 italic truncate max-w-full">
              latest: “{preview.message}”
            </div>
          )}
          <div className="text-[10px] text-amber-100/70 mt-2">
            → Click <span className="font-bold">Save to Github</span> in Emergent, then redeploy to sync.
          </div>
        </div>
        <button
          data-testid="build-status-dismiss"
          onClick={() => setDismissed(true)}
          className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-md bg-amber-800/40 hover:bg-amber-700/60 text-amber-100 font-bold"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
